import React, { CSSProperties, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { SortDirection } from '@tanstack/react-table';
import isNil from 'lodash/isNil';
import { IconWrapper, TableSortIcon } from '../Icon';
import { theme } from '../../styles/theme';
import KubitTooltip from '../KubitTooltip';

interface IProps {
  children: ReactNode;
  sortDirection?: SortDirection | false;
  onClick?: () => void;
  alignment?: CSSProperties['textAlign'];
}

const Container = styled.div`
  line-height: normal;
  font-size: 12px;
  font-weight: 500;
  color: ${theme.colors.secondary900};
  user-select: none;
`;

const SortableContainer = styled(Container)<{
  $alignment?: IProps['alignment'];
}>`
  display: inline-flex;
  align-items: center;
  column-gap: 4px;
  &:hover {
    cursor: pointer;
    color: ${theme.colors.mainBlue};
  }
  ${({ $alignment }) =>
    $alignment === 'right'
      ? css`
          flex-flow: row-reverse;
        `
      : $alignment === 'center' &&
        css`
          justify-content: center;
        `}
`;

const TableHeading = ({
  children,
  sortDirection,
  onClick,
  alignment,
}: IProps) => {
  if (!isNil(sortDirection) && onClick) {
    const sortOrderText = sortDirection === 'asc' ? 'Ascending' : 'Descending';

    return (
      <KubitTooltip
        placement="bottom"
        disabled={sortDirection === false}
        content={sortOrderText}
      >
        <SortableContainer onClick={onClick} $alignment={alignment}>
          {alignment === 'center' && <IconWrapper size={12} />}
          {children}
          <IconWrapper size={12}>
            {sortDirection !== false && (
              <TableSortIcon isSortAsc={sortDirection === 'asc'} />
            )}
          </IconWrapper>
        </SortableContainer>
      </KubitTooltip>
    );
  }

  return <Container>{children}</Container>;
};

export default TableHeading;
